import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { contactKeys } from '@/lib/queryKeys';
import { operationsClient } from '@/lib/api';
import type { Contact, UpdateContactDto } from '@/types/contact';
import { normalizeContactAssignedTags } from '@/lib/contacts/contactTags';
import { deriveDisplayName } from '@/lib/contacts/displayName';
import { normalizeContact } from './useContacts';

export function useUpdateContact() {
  const queryClient = useQueryClient();

  return useApiMutation<Contact, UpdateContactDto>({
    mutationFn: async (dto) => {
      const { id, ...body } = dto;
      const { data } = await operationsClient.patch<Record<string, unknown>>(
        `/contacts/${id}`,
        body,
      );
      return normalizeContact(data);
    },
    onMutate: async (updated) => {
      await queryClient.cancelQueries({ queryKey: contactKeys.detail(updated.id) });
      const previous = queryClient.getQueryData<Contact>(contactKeys.detail(updated.id));
      if (previous) {
        // Mirror the backend's update semantics exactly: a field is changed
        // only when present (`!== undefined`); an explicit `null` clears it.
        // Using `??` here would wrongly keep the previous value when a field is
        // deliberately cleared (e.g. switching commercial→residential clears
        // companyName), making the derived name diverge from the server's.
        const merged: Contact = {
          ...previous,
          firstName: updated.firstName !== undefined ? updated.firstName : previous.firstName,
          lastName: updated.lastName !== undefined ? updated.lastName : previous.lastName,
          companyName:
            updated.companyName !== undefined ? updated.companyName : previous.companyName,
          contactType:
            updated.contactType !== undefined ? updated.contactType : previous.contactType,
        };
        const nextContact: Contact = {
          ...previous,
          ...updated,
          // A null display name means "re-derive on the server". Compute the
          // same derived value locally so views that stay mounted (e.g. the
          // inline identity modal on the contact page) update immediately
          // instead of waiting for the background refetch — the server returns
          // this identical value on success.
          displayName:
            updated.displayName === null
              ? deriveDisplayName(merged) || previous.displayName
              : (updated.displayName ?? previous.displayName),
        };
        if (updated.tagIds !== undefined) {
          nextContact.tags = normalizeContactAssignedTags(undefined, updated.tagIds);
          nextContact.tagIds = updated.tagIds;
        }
        queryClient.setQueryData<Contact>(contactKeys.detail(updated.id), {
          ...nextContact,
        });
      }
      return { previous };
    },
    onError: (_err, updated, context) => {
      const ctx = context as { previous?: Contact } | undefined;
      if (ctx?.previous) {
        queryClient.setQueryData(contactKeys.detail(updated.id), ctx.previous);
      }
    },
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: contactKeys.detail(data.id) });
      if (variables.notes !== undefined) {
        void queryClient.invalidateQueries({ queryKey: contactKeys.notes(data.id) });
      }
      toast.success('Contact updated');
      // NOT-138 — surface the soft warning when the BE flags a duplicate
      // primary phone after the edit.
      if (data.duplicatePhoneWarning) {
        const count = data.duplicatePhoneWarning.existingContactIds.length;
        toast.warning(
          data.duplicatePhoneWarning.message ||
            `Phone number already used by ${count} other contact${count === 1 ? '' : 's'}.`,
        );
      }
    },
  });
}
